import { useEffect, useRef, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Start
} from "@mui/icons-material";
import { RemoveButton } from "../modals";
import { useWindowSize } from "../../core";
import { Cadastro, CadastroData } from "../zas";
import { MenuButton } from "../menuButton";
import { CadastroIconButton } from "./cadastroIconButton";

interface CadastrosTableProps {
  isAdd?: boolean;
  isEditable?: boolean;
  initCadastros?: Cadastro[];
  onChoose?: (cadastro: Cadastro) => void;
  onChange?: (cadastros: Cadastro[]) => void;
}

export const CadastrosTable: React.FC<CadastrosTableProps> = ({
  onChange,
  onChoose,
  isAdd,
  isEditable,
  initCadastros,
}) => {
  const { height, width } = useWindowSize();

  const divRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  const [size, setSize] = useState({ width: 0, height: 0 });
  const [filterSize, setFilterSize] = useState({ width: 0, height: 0 });
  const [titleSize, setTitleSize] = useState({ width: 0, height: 0 });

  const [cadastros, setCadastros] = useState<Cadastro[]>(initCadastros ?? []);
  const [newCadastroLatLon, setNewCadastroLatLon] = useState<[string | number, string | number]>(["", ""]);
  const [newCadastro, setNewCadastro] = useState<CadastroData>({
    id: 0,
    idZas: 0,
    lat: 0,
    lon: 0,
    status: "",
    tipo: "",
    icon: "",
  });


  useEffect(() => {
    if (divRef.current) {
      const { width, height } = divRef.current.getBoundingClientRect();
      setSize({ width, height });
    }
    if (filterRef.current) {
      const { width, height } = filterRef.current.getBoundingClientRect();
      setFilterSize({ width, height });
    }
    if (titleRef.current) {
      const { width, height } = titleRef.current.getBoundingClientRect();
      setTitleSize({ width, height });
    }
  }, [divRef, filterRef, titleRef, height, width]);

  const handleAddCadastro = () => {
    if (
      typeof newCadastroLatLon[0] === "string" ||
      typeof newCadastroLatLon[1] === "string" ||
      newCadastroLatLon[0] === 0 ||
      newCadastroLatLon[1] === 0 ||
      newCadastro.tipo === "" ||
      newCadastro.status === ""
    ) return;

    const cadastro = new Cadastro({
      ...newCadastro,
      lat: newCadastroLatLon[0],
      lon: newCadastroLatLon[1]
    }, {});

    const newCadastros = [...cadastros];
    newCadastros.push(cadastro);
    if (onChange) onChange(newCadastros);
    setCadastros(newCadastros);
    setNewCadastroLatLon(["", ""]);
    setNewCadastro({
      id: 0,
      idZas: 0,
      lat: 0,
      lon: 0,
      status: "",
      tipo: "",
      icon: "",
    });
  };

  const onRemove = (unidade: Cadastro) => {
    const newCadastro = cadastros.filter((ed) => ed !== unidade);
    setCadastros([...newCadastro]);
    if (onChange) onChange(newCadastro);
  };

  return <Box
    ref={divRef}
    sx={{
      flex: 1,
      width: "100%",
      height: "100%",
      padding: "15px",
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    {/* Título */}
    <Box
      ref={titleRef}
      sx={{
        width: "100%",
        paddingTop: "7px",
        paddingX: "15px",
        display: "flex",
        flexDirection: "row",
        alignItems: "start",
        justifyContent: "start",
      }}>
      <Typography fontSize={18} fontWeight={"bold"}>
        {"Cadastros"}
      </Typography>
    </Box>

    {/* Novo Cadastro*/}
    <div
      ref={filterRef}
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Novo Cadastro */}
      {isAdd &&
        <Box sx={{
          flex: 1,
          gap: "15px",
          display: "flex",
          paddingX: "15px",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <Box sx={{
            flex: 1,
            display: "flex",
            paddingX: "15px",
            flexDirection: "column",
          }}>

            {/* Linha 1 */}
            <Box sx={{
              gap: "15px",
              display: "flex",
              flexDirection: "row",
              alignIntems: "center",
              justifyContent: "center",
            }}>

              {/* Lat */}
              <TextField
                fullWidth
                label="Latitude"
                name="lat"
                value={newCadastroLatLon[0].toString().replaceAll(".", ",")}
                onChange={(event) => {
                  const strVal = event.target.value.replaceAll(",", ".");

                  let val: number | string = strVal;
                  if (strVal !== "" && strVal !== "-" && !strVal.endsWith(".")) {
                    val = Number(strVal);
                    if (isNaN(val)) return;
                  }
                  setNewCadastroLatLon([
                    val,
                    newCadastroLatLon[1]
                  ]);
                }}
                variant="standard"
              />

              {/* Lon */}
              <TextField
                fullWidth
                label="Longitude"
                name="lng"
                value={newCadastroLatLon[1].toString().replaceAll(".", ",")}
                onChange={(event) => {
                  const strVal = event.target.value.replaceAll(",", ".");

                  let val: number | string = strVal;
                  if (strVal !== "" && strVal !== "-" && !strVal.endsWith(".")) {
                    val = Number(strVal);
                    if (isNaN(val)) return;
                  }
                  setNewCadastroLatLon([
                    newCadastroLatLon[0],
                    val,
                  ]);
                }}
                variant="standard"
              />

              {/* Tipo */}
              <MenuButton
                onSelect={(val) => setNewCadastro({ ...newCadastro, tipo: val })}
                items={["Residência", "Comércio", "Residência secundária", "Fauna", "Estrutura anexa"]}
              >
                {newCadastro.tipo === "" ? "Tipo" : newCadastro.tipo}
              </MenuButton>

              {/* Status */}
              <MenuButton
                onSelect={(val) => setNewCadastro({ ...newCadastro, status: val })}
                items={["Atendimento", "Vazio/Abandonado", "Recusa", "Não Atendimento", "Em Construção", "Aluga-se/Vende-se"]}
              >
                {newCadastro.status === "" ? "Status" : newCadastro.status}
              </MenuButton>

              {/* Proprietário */}
              <Box sx={{
                minWidth: "150px",
                padding: "0",
                display: "flex",
                alignIntems: "center",
                flexDirection: "column",
                justifyContent: "center",
              }}>
                <TextField
                  fullWidth
                  name="proprietario"
                  variant="standard"
                  label="Proprietário"
                  value={newCadastro.proprietario}
                  onChange={(event) => setNewCadastro({ ...newCadastro, proprietario: event.target.value })}
                  slotProps={{ htmlInput: { maxLength: 250 } }}
                />
                <Typography
                  variant="caption"
                  color={(newCadastro.proprietario ?? "").length > 200 ? "error" : ((newCadastro.proprietario ?? "").length > 150 ? "warning" : undefined)}
                >
                  {(newCadastro.proprietario ?? "").length}/250 caracteres
                </Typography>
              </Box>

              <CadastroIconButton
                onSelect={(icon) => setNewCadastro({ ...newCadastro, icon: `${process.env.REACT_APP_URL}/files/icons_cadastro/${icon}.png` })}
              />
            </Box>

            {/* Linha 2 */}
            <Box sx={{
              gap: "15px",
              display: "flex",
              flexDirection: "row",
              alignIntems: "center",
              justifyContent: "center",
            }}>

              {/* Quantidade de pessoas */}
              <TextField
                fullWidth
                label="Qtd. total de moradores"
                name="qtdTotal"
                value={(newCadastro.qtdPessoas ?? 0).toString()}
                onChange={(event) => {
                  const val = Number(event.target.value);
                  if (isNaN(val)) return;
                  setNewCadastro({
                    ...newCadastro,
                    qtdPessoas: val
                  });
                }}
                variant="standard"
              />

              {/* Quantidade de pessoas com comorbidades */}
              <TextField
                fullWidth
                label="Qtd. moradores com comorbidade"
                name="qtdComorbidade"
                value={(newCadastro.qtdComorbidade ?? 0).toString()}
                onChange={(event) => {
                  const val = Number(event.target.value);
                  if (isNaN(val)) return;
                  setNewCadastro({
                    ...newCadastro,
                    qtdComorbidade: val
                  });
                }}
                variant="standard"
              />

              {/* Quantidade de pessoas com problemas de locomoção */}
              <TextField
                fullWidth
                label="Qtd. moradores com dificuldade de locomoção"
                name="qtdLocomocao"
                value={(newCadastro.qtdLocomocao ?? 0).toString()}
                onChange={(event) => {
                  const val = Number(event.target.value);
                  if (isNaN(val)) return;
                  setNewCadastro({
                    ...newCadastro,
                    qtdLocomocao: val
                  });
                }}
                variant="standard"
              />


              {/* Observações */}
              <TextField
                multiline
                fullWidth
                name="obs_ponto"
                variant="standard"
                label="Observações"
                value={newCadastro.obs}
                onChange={(event) => setNewCadastro({ ...newCadastro, obs: event.target.value })}
              />
            </Box>

          </Box>

          {/* BTN Add */}
          <Box sx={{
            width: "35px",
            height: "35px",
          }}>
            <Tooltip title={"Adicionar Cadastro"}>
              <IconButton onClick={handleAddCadastro}>
                <Add />
              </IconButton>
            </Tooltip>
          </Box>

        </Box>
      }

      <div style={{ height: "15px" }} />
      <Divider />
      <div style={{ height: "15px" }} />
    </div >

    {/* Tabela de estações */}
    <TableContainer sx={{
      flex: 1,
      overflow: "auto",
      height: size.height - filterSize.height - titleSize.height,
      width: size.width
    }}>
      <Table
        stickyHeader
        size="small"
        aria-label="sticky table"
      >
        <TableHead>
          <TableRow>
            {onChoose && <TableCell />}
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />

            <TableCell />
            <TableCell align="center">Quantidade</TableCell>
            <TableCell />
            <TableCell />

            {isEditable && <TableCell />}

          </TableRow>
          <TableRow>
            {onChoose && <TableCell align="left">Selecionar</TableCell>}
            <TableCell align="center">Tipo</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Latitude</TableCell>
            <TableCell align="center">Longitude</TableCell>
            <TableCell align="center">Total</TableCell>
            <TableCell align="center">Comorbidade</TableCell>
            <TableCell align="center">Dificuldade de Locomoção</TableCell>
            <TableCell align="center">Icone</TableCell>
            {isEditable && <TableCell align="right">Remover</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {cadastros.map((row, i) => (
            <TableRow
              key={i}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              {onChoose && <TableCell align="left">
                <IconButton onClick={() => onChoose(row)} >
                  <Start />
                </IconButton>
              </TableCell>}
              <TableCell align="center" sx={{ textOverflow: "ellipsis" }} >{row.properties.tipo}</TableCell>
              <TableCell align="center" sx={{ textOverflow: "ellipsis" }} >{row.properties.status}</TableCell>
              <TableCell align="center" sx={{ textOverflow: "ellipsis" }} >{row.properties.lat.toSignificantDigits("latStr")}</TableCell>
              <TableCell align="center" sx={{ textOverflow: "ellipsis" }} >{row.properties.lon.toSignificantDigits("lngStr")}</TableCell>
              <TableCell align="center" sx={{ textOverflow: "ellipsis" }} >{row.properties.qtdPessoas ?? 0}</TableCell>
              <TableCell align="center" sx={{ textOverflow: "ellipsis" }} >{row.properties.qtdComorbidade ?? 0}</TableCell>
              <TableCell align="center" sx={{ textOverflow: "ellipsis" }} >{row.properties.qtdLocomocao ?? 0}</TableCell>
              <TableCell align="center" sx={{ textOverflow: "ellipsis" }} >
                <img src={row.properties.icon} alt={row.properties.icon} style={{
                  objectFit: "contain",
                  height: `20px`,
                  width: `20px`,
                }} />
              </TableCell>
              {isEditable &&
                <TableCell align="right" sx={{ textOverflow: "ellipsis" }}>
                  <RemoveButton
                    title="Remover Cadastro?"
                    message="Deseja mesmo remover esta cadastro? Esta ação é irreversível!"
                    onRemove={() => onRemove(row)}
                  />
                </TableCell>
              }
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>;
};
